/**
 * Positions ledger + ticket helpers. Pure functions, no React.
 *
 * Gold v2 phase 3b: every figure comes from the position's gold fields
 * (gold.v_position via GET /portfolio/positions) or the Discipline state
 * (gold.position_rule_state). These helpers pick and label fields; they do not
 * compute money, dates or Greeks (policy P5). Missing -> null, shown "—".
 */
import { DisciplineState } from '../types/discipline';
import { GoldPositionFields } from '../types/position';

/** "+$1,234" / "−$56" / "$0" */
export const fmtSigned = (v: number) => {
  const r = Math.round(v);
  return `${r > 0 ? '+' : r < 0 ? '−' : ''}$${Math.abs(r).toLocaleString('en-US')}`;
};

/** "10/16 340C": contract in standard notation (expiry MM/DD, strike, C/P). */
export function contractCode(expiryIso: string | null | undefined, strike: number | null | undefined, isPut: boolean): string {
  if (!expiryIso || strike == null) return '—';
  const [, m, d] = expiryIso.slice(0, 10).split('-');
  const k = Number.isInteger(strike) ? String(strike) : strike.toFixed(2).replace(/0$/, '');
  return `${m}/${d} ${k}${isPut ? 'P' : 'C'}`;
}

/** One rule row, compact: distance = observed − line in the rule's unit. */
export interface RuleRow {
  key: string;
  name: string;
  observed: string;
  line: string;
  distance: string;
  fired: boolean;
  governs: boolean;
  /** The engine's line: the stock price at which the rule fires today. */
  linePrice: number | null;
  /** The New York date it fires if the price and IV stay where they are. */
  triggerDate: string | null;
}

export function ruleRows(d: DisciplineState | null | undefined): RuleRow[] {
  if (!d?.rules) return [];
  const governing = d.fired_rule?.name ?? null;
  return d.rules.map((r) => {
    let distance = '—';
    if (r.observed != null && Number.isFinite(r.observed)) {
      const diff = r.observed - r.threshold;
      const s = diff > 0 ? '+' : diff < 0 ? '−' : '';
      const a = Math.abs(diff);
      switch (r.key) {
        case 'time_stop': distance = `${s}${Math.round(a)}d`; break;
        case 'defensive_delta': distance = `${s}${a.toFixed(2).replace(/^0/, '')}`; break;
        case 'option_stop': distance = `${s}${a.toFixed(1)}×`; break;
        case 'strike_proximity': distance = `${s}${a.toFixed(1)}%`; break;
        case 'earnings': distance = `${s}${Math.round(a)}d`; break;
        case 'ex_dividend': case 'time_value': distance = `${s}$${a.toFixed(2)}`; break;
        default: distance = `${s}${a < 10 ? a.toFixed(1).replace(/\.0$/, '') : Math.round(a)} pt`;
      }
    }
    return {
      key: r.key,
      name: r.name,
      observed: r.observed_display,
      line: r.threshold_display,
      distance,
      fired: r.fired,
      governs: governing != null && r.name === governing,
      linePrice: r.line_price ?? null,
      triggerDate: r.trigger_date ?? null,
    };
  });
}

/** One column of the Hold / Roll / Close grid (whole-position dollars). */
export interface Scenario {
  key: 'hold' | 'roll' | 'close';
  contract: string | null;
  /** Cash the action moves today: + credit, − debit. */
  netCash: number | null;
  /** Option P/L on this leg booked by acting now. */
  locked: number | null;
  /** Option P/L if the (new) contract expires out of the money. */
  atExpiry: number | null;
  /** Position P/L if assigned at the (new) strike. Covered calls only. */
  ifAssigned: number | null;
  strike: number | null;
  /** Share-equivalent delta of the position (glossary net_delta). */
  netDelta: number | null;
  /** Days to expiry (server, New York). */
  dte: number | null;
  /** Next earnings vs this column's expiry. */
  earnings: 'inside' | 'after' | null;
}

export interface ScenarioInput {
  isPut: boolean;
  strike: number;
  expiry: string;
  gold: GoldPositionFields;
  dte: number | null;
  discipline: DisciplineState | null | undefined;
}

/** Hold / Roll / Close from the gold row and the engine's roll target —
 *  every number is a server field (gold.v_position, Discipline decision). */
export function buildScenarios(x: ScenarioInput): Scenario[] {
  const g = x.gold;
  const dec = x.discipline?.decision ?? null;
  const earnings = (inside: boolean | null | undefined): Scenario['earnings'] =>
    inside == null ? null : inside ? 'inside' : 'after';

  const out: Scenario[] = [{
    key: 'hold',
    contract: contractCode(x.expiry, x.strike, x.isPut),
    netCash: 0,
    locked: null,
    atExpiry: g.maxOptionPnl ?? null,                      // glossary max_profit
    ifAssigned: x.isPut ? null : g.pnlIfAssigned ?? null,  // glossary pnl_if_assigned
    strike: x.strike,
    netDelta: g.netDelta ?? null,
    dte: x.dte,
    earnings: earnings(g.earningsInside),
  }];
  const rt = x.discipline?.roll_target ?? null;
  if (rt) {
    const roll = dec?.roll ?? null;
    out.push({
      key: 'roll',
      contract: contractCode(rt.new_expiry, rt.new_strike, x.isPut),
      netCash: roll?.net_credit ?? null,
      locked: g.optionPnl ?? null,                          // the buy-back at the mark books this leg's option P/L
      atExpiry: null,
      ifAssigned: x.isPut ? null : roll?.new_max_profit ?? null,
      strike: rt.new_strike,
      netDelta: null,
      dte: null,
      earnings: rt.crosses_earnings ? 'inside' : null,
    });
  }
  out.push({
    key: 'close',
    contract: null,
    netCash: g.buybackCost != null ? -g.buybackCost : null,  // glossary buyback_cost, paid
    locked: g.optionPnl ?? null,
    atExpiry: g.optionPnl ?? null,
    ifAssigned: null,
    strike: null,
    netDelta: null,
    dte: null,
    earnings: null,
  });
  return out;
}

export type LedgerFilter = 'all' | 'risk' | 'clock' | 'goal' | 'quiet';

/** Nearest-line proximity for the rule glyph: 0–1 toward the closest unfired
 *  line (1 = on it), plus whether a rule has fired. null progress = unknown.
 *  A visual encoding of the engine's observed vs line (no dates, no prices). */
export function ruleProximity(
  d: DisciplineState | null | undefined,
): { fired: boolean; progress: number | null; nearest: string | null } {
  if (!d) return { fired: false, progress: null, nearest: null };
  const fired = d.fired_rule != null;
  let best: { p: number; name: string } | null = null;
  for (const r of d.rules ?? []) {
    if (r.fired || r.observed == null || !Number.isFinite(r.observed) || !(r.threshold > 0)) continue;
    // time stop counts days down toward its line; the others fire at observed ≥ line
    const p = Math.max(0, Math.min(1, r.key === 'time_stop' ? r.threshold / Math.max(r.observed, r.threshold) : Math.max(0, r.observed) / r.threshold));
    if (!best || p > best.p) best = { p, name: r.name };
  }
  return { fired, progress: best ? best.p : fired ? 1 : null, nearest: best?.name ?? null };
}

/** One line of "Total, explained" (glossary pnl_explained). */
export interface ExplainedLine {
  key: string;
  label: string;
  value: number | null;
  /** Level 2 lines (time decay / volatility / price effect) sit under Premium income. */
  sub?: boolean;
}

/** Total, explained — two levels, all server figures (gold.v_position):
 *    Total = Shares (stock P&L) + Premium income + Cost of capped upside + Dividends
 *    Premium income = Time decay + Volatility effect + Price effect
 *  A put has no shares (no stock line). Null when the row is unknown. */
export function explainedLines(g: GoldPositionFields, isPut: boolean): ExplainedLine[] | null {
  if (g.totalPnl == null) return null;
  const lines: ExplainedLine[] = [];
  if (!isPut) lines.push({ key: 'stock', label: 'Shares', value: g.stockPnl ?? null });
  lines.push({ key: 'premium_income', label: 'Premium income', value: g.premiumIncome ?? null });
  if (g.timeDecay != null || g.ivEffect != null || g.priceEffect != null) {
    lines.push({ key: 'time_decay', label: 'Time decay', value: g.timeDecay ?? null, sub: true });
    lines.push({ key: 'iv_effect', label: 'Volatility', value: g.ivEffect ?? null, sub: true });
    lines.push({ key: 'price_effect', label: 'Price effect', value: g.priceEffect ?? null, sub: true });
  }
  if (g.costOfCappedUpside != null && g.costOfCappedUpside !== 0) {
    lines.push({ key: 'capped', label: 'Cost of capped upside', value: g.costOfCappedUpside });
  }
  if (g.dividends != null && g.dividends !== 0) lines.push({ key: 'dividends', label: 'Dividends', value: g.dividends });
  return lines;
}
